// src/pages/SearchResultsPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { productService } from '../services/productService';
import ProductCard from '../components/Products/ProductCard';

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get('query') || '';
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!query) {
      setLoading(false);
      return;
    }
    
    const fetchResults = async () => {
      setLoading(true);
      try {
        const data = await productService.searchProducts(query);
        // Extract variants from the search response
        setResults(data?.outletsVariants || []);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResults();
  }, [query]);

  return (
    <div className="min-h-screen bg-white w-full py-6 px-4 md:px-8 max-w-7xl mx-auto">
      
      {/* Search Header */}
      <div className="flex items-center gap-3 mb-8 border-b border-gray-100 pb-4">
        <button 
          onClick={() => navigate(-1)} 
          className="w-10 h-10 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
            Search Results
          </h1>
          <p className="text-sm text-gray-500 mt-1 font-medium">
            Showing results for <span className="text-[#0078ad] font-bold">"{query}"</span>
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-32">
          <i className="fas fa-spinner fa-spin text-4xl text-[#0078ad] mb-4"></i>
          <p className="text-gray-500 font-bold">Fetching matching products...</p>
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-24 bg-gray-50/50 rounded-3xl border border-gray-100 border-dashed">
          <i className="fas fa-search text-6xl text-gray-200 mb-4"></i>
          <h3 className="text-2xl text-gray-800 font-black mb-2">No items found</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            We couldn't find any products matching "{query}". Try checking for typos or searching with different keywords.
          </p>
          <button 
            onClick={() => navigate('/quick')}
            className="mt-6 bg-[#0078ad] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-sky-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {results.map((variant) => (
              <ProductCard 
                key={variant.outletVariantId || variant.id || variant.productId} 
                product={variant} 
                addToCart={addToCart} 
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;